import { Link } from 'react-router-dom';

const lastUpdated = 'September 13, 2026';

export function QuickbooksN8nPrivacyPage() {
  return (
    <div className="container legal-page">
      <div className="legal-page-header">
        <span className="eyebrow">Privacy Policy</span>
        <h1>QuickBooks Online &amp; n8n Integration Privacy Policy</h1>
        <p className="legal-page-sub">Last updated: {lastUpdated}</p>
      </div>

      <div className="legal-page-intro">
        <p>
          This Privacy Policy describes how Bizi Office (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or
          &ldquo;our&rdquo;) collects, uses, and protects data when integrating QuickBooks Online
          with n8n workflow automation. By connecting your QuickBooks Online account to our n8n
          integration, you agree to the practices described below.
        </p>
      </div>

      <nav className="legal-toc" aria-label="Table of contents">
        <h2>Contents</h2>
        <ul>
          <li><a href="#data-collection">Data Collection</a></li>
          <li><a href="#data-usage">Data Usage</a></li>
          <li><a href="#data-sharing">Data Sharing</a></li>
          <li><a href="#security">Security</a></li>
          <li><a href="#contact">Contact &amp; Changes</a></li>
        </ul>
      </nav>

      <section id="data-collection" className="legal-section">
        <h2>Data Collection</h2>
        <p>
          When you authorize our n8n integration to connect to your QuickBooks Online account, we
          collect only the data necessary to perform the automation workflows you have requested.
          This may include:
        </p>
        <ul className="legal-list">
          <li>Company and chart-of-accounts metadata required to route workflow data</li>
          <li>Customer, vendor, and invoice records that are processed by your configured workflows</li>
          <li>Transaction data such as payments, bills, and expenses that your workflows read or write</li>
          <li>OAuth authentication tokens issued by QuickBooks Online to authorize API access</li>
          <li>Workflow execution logs generated by n8n for troubleshooting and auditing</li>
        </ul>
        <p>
          We do not collect more data than is required to execute the specific automation workflows
          you have designed. You control which QuickBooks Online entities and fields are accessed by
          configuring the scope of each n8n workflow node.
        </p>
      </section>

      <section id="data-usage" className="legal-section">
        <h2>Data Usage</h2>
        <p>
          We use the data collected through this integration <strong>exclusively to automate and
          synchronize your internal systems</strong>. Specifically:
        </p>
        <ul className="legal-list">
          <li>
            <strong>Workflow Automation:</strong> Data from QuickBooks Online is read, transformed,
            and routed to your connected systems (such as EspoCRM, spreadsheets, email, or other
            integrated platforms) based on the workflow logic you configure in n8n.
          </li>
          <li>
            <strong>System Synchronization:</strong> We synchronize records between QuickBooks
            Online and your other business tools so that data stays current and consistent across
            your operational stack.
          </li>
          <li>
            <strong>Error Handling &amp; Logging:</strong> n8n generates execution logs that we use
            to diagnose workflow failures, retry failed operations, and ensure reliable data
            delivery.
          </li>
          <li>
            <strong>Support &amp; Maintenance:</strong> If you request assistance, we may review
            workflow execution logs to troubleshoot and resolve issues.
          </li>
        </ul>
        <p>
          We do not use your QuickBooks Online data for marketing, advertising, product development,
          or any purpose unrelated to the automation workflows you have configured.
        </p>
      </section>

      <section id="data-sharing" className="legal-section">
        <h2>Data Sharing</h2>
        <p>
          <strong>We do not sell, rent, or share your QuickBooks Online data with third parties.</strong>
        </p>
        <ul className="legal-list">
          <li>
            Your data is not sold to data brokers, advertisers, or analytics providers.
          </li>
          <li>
            Your data is not shared with third-party marketing or sales platforms.
          </li>
          <li>
            Data is only transmitted between QuickBooks Online and the specific destination systems
            you have configured within your n8n instance. You choose where data goes; we do not
            route it elsewhere.
          </li>
          <li>
            We do not use your data to train AI models, build aggregate datasets, or create derived
            analytics for any external audience.
          </li>
        </ul>
        <p>
          The only exception is where required by law, court order, or legal process, in which case
          we will disclose only the minimum data necessary to comply with the legal requirement.
        </p>
      </section>

      <section id="security" className="legal-section">
        <h2>Security</h2>
        <p>
          We take the security of your QuickBooks Online data seriously. The following measures are
          in place:
        </p>
        <ul className="legal-list">
          <li>
            <strong>Encrypted Credential Storage:</strong> Your QuickBooks Online OAuth tokens and
            API keys are encrypted at rest and stored within a secure, unique n8n instance
            dedicated to your environment. Credentials are never stored in plaintext, shared
            between tenants, or exported to external systems.
          </li>
          <li>
            <strong>Dedicated Instance Isolation:</strong> Each n8n integration runs in a unique
            instance with its own credential store, execution environment, and access controls.
            Your workflows and data are isolated from other clients&rsquo; environments.
          </li>
          <li>
            <strong>Encrypted Data Transmission:</strong> All API communication between n8n and
            QuickBooks Online is encrypted in transit using TLS/HTTPS. No data is transmitted over
            unencrypted channels.
          </li>
          <li>
            <strong>Access Controls:</strong> Access to the n8n instance and its credential store is
            restricted to authorized personnel with a legitimate need. Administrative access is
            protected by strong authentication and least-privilege principles.
          </li>
          <li>
            <strong>Execution Logging:</strong> Workflow execution logs are retained for
            troubleshooting and audit purposes and are accessible only to authorized users within
            your n8n instance.
          </li>
          <li>
            <strong>No Data Retention Beyond Need:</strong> We do not retain QuickBooks Online data
            beyond what is necessary for the operation of your workflows. When a workflow is
            removed or an integration is disconnected, associated cached data and credentials are
            purged from the n8n instance.
          </li>
        </ul>
      </section>

      <section id="contact" className="legal-section">
        <h2>Contact &amp; Changes</h2>
        <p>
          If you have questions about this Privacy Policy or our QuickBooks Online &amp; n8n
          integration practices, please <Link to="/#contact" className="legal-link">contact us</Link>.
        </p>
        <p>
          We may update this Privacy Policy from time to time. The &ldquo;Last updated&rdquo; date
          at the top of this page reflects the most recent revision. We encourage you to review this
          page periodically to stay informed about how we protect your data.
        </p>
      </section>

      <div className="legal-page-footer">
        <Link to="/" className="btn btn-ghost">&larr; Back to Home</Link>
      </div>
    </div>
  );
}
